import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, interval } from 'rxjs';
import { map, filter } from 'rxjs/operators';

/**
 * Performance metrics interface
 */
interface PerformanceMetrics {
    loadTime: number;
    cacheHitRatio: number;
    memoryUsage: number;
    errorRate: number;
    languageUsage: { [key: string]: number };
}

/**
 * Performance monitoring configuration
 */
interface PerformanceConfig {
    enableMonitoring: boolean;
    monitoringInterval: number; // in milliseconds
    maxMetricsHistory: number;
    enableAutoOptimization: boolean;
    optimizationThreshold: number; // memory usage threshold for auto-optimization
}

/**
 * Service for monitoring and optimizing translation performance
 */
@Injectable({
    providedIn: 'root',
})
export class TranslationPerformanceService {
    private metricsSubject = new BehaviorSubject<PerformanceMetrics>({
        loadTime: 0,
        cacheHitRatio: 0,
        memoryUsage: 0,
        errorRate: 0,
        languageUsage: {},
    });

    private metricsHistory: PerformanceMetrics[] = [];
    private config: PerformanceConfig;
    private loadTimes: number[] = [];
    private errorCount = 0;
    private totalRequests = 0;
    private languageUsageCount: { [key: string]: number } = {};

    public metrics$ = this.metricsSubject.asObservable();

    constructor() {
        this.config = {
            enableMonitoring: true,
            monitoringInterval: 30000, // 30 seconds
            maxMetricsHistory: 100,
            enableAutoOptimization: true,
            optimizationThreshold: 5 * 1024 * 1024, // 5MB
        };

        this.initializeMonitoring();
    }

    /**
     * Initialize performance monitoring
     */
    private initializeMonitoring(): void {
        if (!this.config.enableMonitoring) {
            return;
        }

        // Set up periodic metrics collection
        interval(this.config.monitoringInterval).subscribe(() => {
            this.collectMetrics();
        });

        console.log('Translation performance monitoring initialized');
    }

    /**
     * Record translation load time
     */
    recordLoadTime(language: string, loadTime: number): void {
        this.loadTimes.push(loadTime);
        this.totalRequests++;

        // Track language usage
        this.languageUsageCount[language] =
            (this.languageUsageCount[language] || 0) + 1;

        // Keep only recent load times (last 100)
        if (this.loadTimes.length > 100) {
            this.loadTimes = this.loadTimes.slice(-100);
        }

        console.log(
            `Translation load time recorded for ${language}: ${loadTime}ms`
        );
    }

    /**
     * Record translation error
     */
    recordError(language: string, error: any): void {
        this.errorCount++;
        console.error(`Translation error recorded for ${language}:`, error);
    }

    /**
     * Update cache hit ratio
     */
    updateCacheHitRatio(ratio: number): void {
        const currentMetrics = this.metricsSubject.value;
        this.metricsSubject.next({
            ...currentMetrics,
            cacheHitRatio: ratio,
        });
    }

    /**
     * Update memory usage
     */
    updateMemoryUsage(usage: number): void {
        const currentMetrics = this.metricsSubject.value;
        this.metricsSubject.next({
            ...currentMetrics,
            memoryUsage: usage,
        });

        // Check for auto-optimization
        if (
            this.config.enableAutoOptimization &&
            usage > this.config.optimizationThreshold
        ) {
            this.triggerOptimization();
        }
    }

    /**
     * Collect current performance metrics
     */
    private collectMetrics(): void {
        const averageLoadTime =
            this.loadTimes.length > 0
                ? this.loadTimes.reduce((sum, time) => sum + time, 0) /
                  this.loadTimes.length
                : 0;

        const errorRate =
            this.totalRequests > 0
                ? (this.errorCount / this.totalRequests) * 100
                : 0;

        const currentMetrics = this.metricsSubject.value;
        const newMetrics: PerformanceMetrics = {
            loadTime: averageLoadTime,
            cacheHitRatio: currentMetrics.cacheHitRatio,
            memoryUsage: currentMetrics.memoryUsage,
            errorRate,
            languageUsage: { ...this.languageUsageCount },
        };

        this.metricsSubject.next(newMetrics);
        this.addToHistory(newMetrics);

        console.log('Performance metrics collected:', newMetrics);
    }

    /**
     * Add metrics to history
     */
    private addToHistory(metrics: PerformanceMetrics): void {
        this.metricsHistory.push(metrics);

        // Keep only recent history
        if (this.metricsHistory.length > this.config.maxMetricsHistory) {
            this.metricsHistory = this.metricsHistory.slice(
                -this.config.maxMetricsHistory
            );
        }
    }

    /**
     * Get performance metrics history
     */
    getMetricsHistory(): PerformanceMetrics[] {
        return [...this.metricsHistory];
    }

    /**
     * Get performance summary
     */
    getPerformanceSummary(): {
        averageLoadTime: number;
        peakMemoryUsage: number;
        totalErrors: number;
        mostUsedLanguage: string;
        cacheEfficiency: string;
    } {
        const history = this.metricsHistory;

        if (history.length === 0) {
            return {
                averageLoadTime: 0,
                peakMemoryUsage: 0,
                totalErrors: 0,
                mostUsedLanguage: 'none',
                cacheEfficiency: 'unknown',
            };
        }

        const averageLoadTime =
            history.reduce((sum, m) => sum + m.loadTime, 0) / history.length;
        const peakMemoryUsage = Math.max(...history.map((m) => m.memoryUsage));
        const averageCacheHitRatio =
            history.reduce((sum, m) => sum + m.cacheHitRatio, 0) /
            history.length;

        // Find most used language
        const languageUsage = this.languageUsageCount;
        const mostUsedLanguage = Object.keys(languageUsage).reduce(
            (a, b) => (languageUsage[a] > languageUsage[b] ? a : b),
            'none'
        );

        // Determine cache efficiency
        let cacheEfficiency = 'poor';
        if (averageCacheHitRatio > 0.8) cacheEfficiency = 'excellent';
        else if (averageCacheHitRatio > 0.6) cacheEfficiency = 'good';
        else if (averageCacheHitRatio > 0.4) cacheEfficiency = 'fair';

        return {
            averageLoadTime: Math.round(averageLoadTime),
            peakMemoryUsage: Math.round(peakMemoryUsage),
            totalErrors: this.errorCount,
            mostUsedLanguage,
            cacheEfficiency,
        };
    }

    /**
     * Trigger optimization
     */
    private triggerOptimization(): void {
        console.log('Auto-optimization triggered due to high memory usage');
        // This would typically call the translation service optimization methods
        // For now, we'll just log the event
    }

    /**
     * Reset performance metrics
     */
    resetMetrics(): void {
        this.loadTimes = [];
        this.errorCount = 0;
        this.totalRequests = 0;
        this.languageUsageCount = {};
        this.metricsHistory = [];

        this.metricsSubject.next({
            loadTime: 0,
            cacheHitRatio: 0,
            memoryUsage: 0,
            errorRate: 0,
            languageUsage: {},
        });

        console.log('Performance metrics reset');
    }

    /**
     * Update configuration
     */
    updateConfig(newConfig: Partial<PerformanceConfig>): void {
        this.config = { ...this.config, ...newConfig };
        console.log(
            'Performance monitoring configuration updated:',
            this.config
        );
    }

    /**
     * Get current configuration
     */
    getConfig(): PerformanceConfig {
        return { ...this.config };
    }

    /**
     * Check if performance is degraded
     */
    isPerformanceDegraded(): boolean {
        const currentMetrics = this.metricsSubject.value;

        return (
            currentMetrics.loadTime > 1000 || // Load time > 1 second
            currentMetrics.errorRate > 5 || // Error rate > 5%
            currentMetrics.cacheHitRatio < 0.3 || // Cache hit ratio < 30%
            currentMetrics.memoryUsage > this.config.optimizationThreshold
        );
    }

    /**
     * Get performance recommendations
     */
    getPerformanceRecommendations(): string[] {
        const recommendations: string[] = [];
        const currentMetrics = this.metricsSubject.value;

        if (currentMetrics.loadTime > 1000) {
            recommendations.push(
                'Consider preloading frequently used translations'
            );
        }

        if (currentMetrics.cacheHitRatio < 0.5) {
            recommendations.push(
                'Increase cache size or timeout to improve hit ratio'
            );
        }

        if (currentMetrics.memoryUsage > this.config.optimizationThreshold) {
            recommendations.push(
                'Optimize cache by removing unused translations'
            );
        }

        if (currentMetrics.errorRate > 3) {
            recommendations.push(
                'Check translation file integrity and network connectivity'
            );
        }

        if (recommendations.length === 0) {
            recommendations.push('Performance is optimal');
        }

        return recommendations;
    }

    /**
     * Export performance data for analysis
     */
    exportPerformanceData(): {
        summary: any;
        history: PerformanceMetrics[];
        config: PerformanceConfig;
        timestamp: number;
    } {
        return {
            summary: this.getPerformanceSummary(),
            history: this.getMetricsHistory(),
            config: this.getConfig(),
            timestamp: Date.now(),
        };
    }
}
